import { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import { tokens } from '../../components/forms/tokens';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime, fromLocalInput, toLocalInput } from '../../lib/datetime';
import maintenanceService, {
  type MaintenanceWindow,
  type ScheduleWindowPayload,
} from '../../services/maintenance.service';
import { colors } from '../../styles/theme';

/**
 * Super-admin CRUD for maintenance windows.
 *
 * The form takes wall-clock local datetimes (datetime-local inputs) and
 * converts them to ISO/UTC at submit time. Defaults nudge the operator
 * toward sane offsets — banner shows 2h before start, email goes 24h
 * before — so the most common path is "type the outage time + reason
 * and submit".
 */

const MS_HOUR = 3_600_000;

function nowRoundedToHour(): Date {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  return d;
}

interface FormState {
  starts_at: string;
  ends_at: string;
  email_send_at: string;
  banner_show_from: string;
  reason_short: string;
  reason_long: string;
}

function defaultForm(): FormState {
  const start = new Date(nowRoundedToHour().getTime() + 48 * MS_HOUR);
  const end = new Date(start.getTime() + 2 * MS_HOUR);
  const banner = new Date(start.getTime() - 2 * MS_HOUR);
  const email = new Date(start.getTime() - 24 * MS_HOUR);
  return {
    starts_at: toLocalInput(start),
    ends_at: toLocalInput(end),
    email_send_at: toLocalInput(email),
    banner_show_from: toLocalInput(banner),
    reason_short: '',
    reason_long: '',
  };
}

function statusTone(w: MaintenanceWindow): { tone: BadgeTone; label: string } {
  if (w.status === 'cancelled') return { tone: 'danger', label: 'Cancelled' };
  if (w.status === 'completed') return { tone: 'neutral', label: 'Completed' };
  const now = Date.now();
  const start = new Date(w.starts_at).getTime();
  const end = new Date(w.ends_at).getTime();
  if (now >= end) return { tone: 'neutral', label: 'Past' };
  if (now >= start) return { tone: 'warning', label: 'In progress' };
  return { tone: 'info', label: 'Scheduled' };
}

export default function AdminMaintenance(): JSX.Element {
  const [windows, setWindows] = useState<MaintenanceWindow[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>(defaultForm);
  const [submitting, setSubmitting] = useState(false);
  const [formErr, setFormErr] = useState<string | null>(null);

  const [cancelTarget, setCancelTarget] = useState<MaintenanceWindow | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const items = await maintenanceService.listWindows();
      setWindows(items);
    } catch (e) {
      setErr(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const validation = useMemo<string | null>(() => {
    try {
      const s = fromLocalInput(form.starts_at);
      const e = fromLocalInput(form.ends_at);
      const em = fromLocalInput(form.email_send_at);
      const b = fromLocalInput(form.banner_show_from);
      if (!form.reason_short.trim()) return 'Short reason is required.';
      if (form.reason_short.length > 200) return 'Short reason must be ≤ 200 chars.';
      if (s.getTime() >= e.getTime()) return 'Start must be before end.';
      if (em.getTime() > s.getTime()) return 'Email must be sent on or before start.';
      if (b.getTime() > s.getTime()) return 'Banner must show on or before start.';
      return null;
    } catch {
      return 'Check the dates entered.';
    }
  }, [form]);

  const submit = async (): Promise<void> => {
    if (validation) {
      setFormErr(validation);
      return;
    }
    setSubmitting(true);
    setFormErr(null);
    try {
      const payload: ScheduleWindowPayload = {
        starts_at: fromLocalInput(form.starts_at).toISOString(),
        ends_at: fromLocalInput(form.ends_at).toISOString(),
        email_send_at: fromLocalInput(form.email_send_at).toISOString(),
        banner_show_from: fromLocalInput(form.banner_show_from).toISOString(),
        reason_short: form.reason_short.trim(),
        reason_long: form.reason_long.trim() || null,
      };
      await maintenanceService.schedule(payload);
      setForm(defaultForm());
      setFlash('Maintenance window scheduled.');
      reload();
      globalThis.setTimeout(() => setFlash(null), 5000);
    } catch (e) {
      setFormErr(extractError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Maintenance windows"
        subtitle="Schedule planned downtime. Banners and email notifications fan out automatically based on the times below."
      />

      {flash && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          style={{ background: tokens.successBg, border: `1px solid ${tokens.successBorder}`, color: tokens.successText }}
        >
          {flash}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        {/* ── Form ────────────────────────────────────────────── */}
        <div className="rounded-2xl p-5" style={{ background: tokens.surface }}>
          <h2 className="text-base font-semibold mb-4" style={{ color: tokens.textPrimary }}>
            Schedule a new window
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Starts at">
              <input
                type="datetime-local"
                value={form.starts_at}
                onChange={(e) => setForm((f) => ({ ...f, starts_at: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ border: `1px solid ${tokens.border}` }}
              />
            </Field>
            <Field label="Ends at">
              <input
                type="datetime-local"
                value={form.ends_at}
                onChange={(e) => setForm((f) => ({ ...f, ends_at: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ border: `1px solid ${tokens.border}` }}
              />
            </Field>
            <Field label="Email send at">
              <input
                type="datetime-local"
                value={form.email_send_at}
                onChange={(e) => setForm((f) => ({ ...f, email_send_at: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ border: `1px solid ${tokens.border}` }}
              />
            </Field>
            <Field label="Banner shows from">
              <input
                type="datetime-local"
                value={form.banner_show_from}
                onChange={(e) => setForm((f) => ({ ...f, banner_show_from: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ border: `1px solid ${tokens.border}` }}
              />
            </Field>
          </div>

          <div className="mt-3">
            <Field label="Short reason (banner & email subject)">
              <input
                type="text"
                value={form.reason_short}
                onChange={(e) => setForm((f) => ({ ...f, reason_short: e.target.value }))}
                maxLength={200}
                placeholder="e.g. Database upgrade to PG 15"
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ border: `1px solid ${tokens.border}` }}
              />
            </Field>
          </div>

          <div className="mt-3">
            <Field label="Long reason (email body, optional)">
              <textarea
                value={form.reason_long}
                onChange={(e) => setForm((f) => ({ ...f, reason_long: e.target.value }))}
                rows={4}
                maxLength={5000}
                placeholder="Plain text. Markdown is not rendered."
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ border: `1px solid ${tokens.border}` }}
              />
            </Field>
          </div>

          {(formErr || validation) && (
            <div
              className="mt-3 p-3 rounded-lg text-sm"
              style={{ background: tokens.errorBg, border: `1px solid ${tokens.errorBorder}`, color: tokens.errorText }}
            >
              {formErr ?? validation}
            </div>
          )}

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setForm(defaultForm())}
              className="px-4 py-2 rounded-lg text-sm font-semibold"
              style={{ background: tokens.surfaceMuted, color: tokens.textPrimary }}
            >
              Reset
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={submitting || validation !== null}
              className="px-4 py-2 rounded-lg text-sm font-semibold"
              style={{
                background: submitting || validation ? tokens.disabledBg : tokens.infoText,
                color: submitting || validation ? tokens.disabledText : '#FFFFFF',
                cursor: submitting || validation ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Scheduling…' : 'Schedule window'}
            </button>
          </div>
        </div>

        {/* ── Banner preview ──────────────────────────────────── */}
        <div className="rounded-2xl p-5" style={{ background: tokens.surface }}>
          <h2 className="text-base font-semibold mb-4" style={{ color: tokens.textPrimary }}>
            Banner preview
          </h2>
          <BannerPreview reasonShort={form.reason_short} startsAt={form.starts_at} />
          <p className="text-xs mt-3" style={{ color: tokens.textMuted }}>
            Live preview of the in-app banner copy. Once scheduled, it
            appears for every authenticated user from{' '}
            <strong>{form.banner_show_from ? formatDateTime(fromLocalInput(form.banner_show_from).toISOString()) : '—'}</strong>{' '}
            and the countdown updates every second.
          </p>
        </div>
      </div>

      {/* ── List ──────────────────────────────────────────────── */}
      <div className="rounded-2xl p-5" style={{ background: tokens.surface }}>
        <h2 className="text-base font-semibold mb-4" style={{ color: tokens.textPrimary }}>
          Existing windows
        </h2>

        {err && (
          <div
            className="mb-4 p-3 rounded-lg text-sm"
            style={{ background: tokens.errorBg, border: `1px solid ${tokens.errorBorder}`, color: tokens.errorText }}
          >
            {err}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ color: tokens.textMuted, borderBottom: `1px solid ${tokens.border}` }}>
                <th className="text-left py-2 pr-3">Window</th>
                <th className="text-left py-2 pr-3">Banner from</th>
                <th className="text-left py-2 pr-3">Email sent</th>
                <th className="text-left py-2 pr-3">Reason</th>
                <th className="text-left py-2 pr-3">Status</th>
                <th className="text-right py-2 pr-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="py-6 text-center" style={{ color: tokens.textMuted }}>Loading…</td></tr>
              )}
              {!loading && windows.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center" style={{ color: tokens.textMuted }}>No maintenance windows yet.</td></tr>
              )}
              {windows.map((w) => {
                const tone = statusTone(w);
                const cancellable = w.status === 'scheduled' && new Date(w.ends_at).getTime() > Date.now();
                return (
                  <tr key={w.id} style={{ borderBottom: `1px solid ${tokens.border}` }}>
                    <td className="py-3 pr-3">
                      <div style={{ color: tokens.textPrimary }}>{formatDateTime(w.starts_at)}</div>
                      <div className="text-xs" style={{ color: tokens.textMuted }}>→ {formatDateTime(w.ends_at)}</div>
                    </td>
                    <td className="py-3 pr-3 text-xs" style={{ color: tokens.textMuted }}>
                      {formatDateTime(w.banner_show_from)}
                    </td>
                    <td className="py-3 pr-3 text-xs" style={{ color: tokens.textMuted }}>
                      {w.email_sent_at ? formatDateTime(w.email_sent_at) : `pending — ${formatDateTime(w.email_send_at)}`}
                    </td>
                    <td className="py-3 pr-3" style={{ color: tokens.textPrimary }}>
                      <div className="font-medium">{w.reason_short}</div>
                      {w.reason_long && (
                        <div className="text-xs whitespace-pre-line line-clamp-2" style={{ color: tokens.textMuted }}>
                          {w.reason_long}
                        </div>
                      )}
                    </td>
                    <td className="py-3 pr-3">
                      <StatusBadge tone={tone.tone}>{tone.label}</StatusBadge>
                    </td>
                    <td className="py-3 pr-3 text-right">
                      {cancellable ? (
                        <button
                          type="button"
                          onClick={() => setCancelTarget(w)}
                          className="px-3 py-1.5 rounded-md text-xs font-semibold"
                          style={{ background: tokens.errorBg, color: tokens.errorText, border: `1px solid ${tokens.errorBorder}` }}
                        >
                          Cancel
                        </button>
                      ) : (
                        <span className="text-xs" style={{ color: tokens.textMuted }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {cancelTarget && (
        <CancelDialog
          window={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onDone={(msg) => {
            setCancelTarget(null);
            setFlash(msg);
            reload();
            globalThis.setTimeout(() => setFlash(null), 5000);
          }}
        />
      )}
    </>
  );
}

function Field({ label, children }: Readonly<{ label: string; children: React.ReactNode }>): JSX.Element {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function BannerPreview({
  reasonShort,
  startsAt,
}: Readonly<{
  reasonShort: string;
  startsAt: string;
}>): JSX.Element {
  const reason = reasonShort.trim() || '<reason will appear here>';
  let countdown = '—';
  try {
    const ms = Math.max(0, fromLocalInput(startsAt).getTime() - Date.now());
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const minutesAndSeconds = m > 0
      ? `${m}m ${String(s).padStart(2, '0')}s`
      : `${s}s`;
    countdown = h > 0
      ? `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`
      : minutesAndSeconds;
  } catch {
    /* ignore */
  }

  return (
    <div
      style={{
        background: '#FFF7E6',
        borderBottom: `1px solid ${colors.borderSoft}`,
        color: colors.ink,
        borderRadius: 12,
        border: `1px solid ${colors.border}`,
      }}
    >
      <div className="px-4 py-2 flex items-center gap-3 text-sm">
        <span
          aria-hidden
          style={{
            display: 'inline-flex',
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: colors.warning,
            flexShrink: 0,
          }}
        />
        <p className="leading-tight">
          Scheduled maintenance — {reason}. Begins in{' '}
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{countdown}</span>
          {/* */}. Please save your work.
        </p>
      </div>
    </div>
  );
}

function CancelDialog({
  window: w,
  onClose,
  onDone,
}: Readonly<{
  window: MaintenanceWindow;
  onClose: () => void;
  onDone: (msg: string) => void;
}>): JSX.Element {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await maintenanceService.cancel(w.id, reason.trim() || null);
      onDone('Maintenance window cancelled.');
    } catch (e) {
      setErr(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} busy={busy} maxWidth="lg">
      <h2 className="text-lg font-semibold mb-1" style={{ color: tokens.textPrimary }}>
        Cancel maintenance window
      </h2>
      <p className="text-sm mb-4" style={{ color: tokens.textMuted }}>
        The banner will stop showing immediately and any pending email
        notifications will be dropped. This cannot be undone — schedule
        a new window if needed.
      </p>

      <div
        className="mb-4 p-3 rounded-lg text-xs"
        style={{ background: tokens.surfaceMuted, color: tokens.textPrimary }}
      >
        <div><strong>{w.reason_short}</strong></div>
        <div style={{ color: tokens.textMuted }}>
          {formatDateTime(w.starts_at)} → {formatDateTime(w.ends_at)}
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="cancel-window-reason" className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>
          Reason (optional · stored in audit log)
        </label>
        <textarea
          id="cancel-window-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          maxLength={500}
          className="w-full px-3 py-2 rounded-lg text-sm"
          style={{ border: `1px solid ${tokens.border}` }}
          placeholder="e.g. Postponed to next quarter"
        />
      </div>

      {err && (
        <div
          className="mb-3 p-3 rounded-lg text-sm"
          style={{ background: tokens.errorBg, border: `1px solid ${tokens.errorBorder}`, color: tokens.errorText }}
        >
          {err}
        </div>
      )}

      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{ background: tokens.surfaceMuted, color: tokens.textPrimary }}
        >
          Keep window
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="px-4 py-2 rounded-lg text-sm font-semibold"
          style={{
            background: busy ? tokens.disabledBg : tokens.errorText,
            color: busy ? tokens.disabledText : '#FFFFFF',
            cursor: busy ? 'not-allowed' : 'pointer',
          }}
        >
          {busy ? 'Cancelling…' : 'Cancel window'}
        </button>
      </div>
    </Modal>
  );
}

function extractError(e: unknown): string {
  return getApiErrorMessage(e, 'Unexpected error');
}
